"use client"

import {BrowserRouter, Route, Routes, Navigate, useNavigate} from 'react-router-dom';
import {Provider} from './components/ui/provider';
import theme from './theme';
import Landing from './pages/Landing';
import './App.css';
import {useEffect, useState} from 'react';
import {useAtom} from 'jotai';
import userAtom from './atoms/userAtom';
import authenticatedAtom from './atoms/authenticatedAtom';
import {Toaster} from "./components/ui/toaster"
import {Box, Center, Flex, Spinner, VStack, Image, useMediaQuery} from "@chakra-ui/react";
import DemoSidebar from "./components/demo/DemoSidebar";
import DemoHeader from "./components/demo/DemoHeader";
import DemoFooter from "./components/demo/DemoFooter";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import DemoProfile from "./pages/demo/DemoProfile";
import DemoFinancialAnalysis from "./components/demo/DemoFinancialAnalysis";
import DemoFarmerDashboard from "./components/demo/DemoFarmerDashboard";
import DemoSatelliteImagery from "./components/demo/DemoSatelliteImagery";
import DemoWeather from "./components/demo/DemoWeather";

const baseUrl = process.env.REACT_APP_API_URL;
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";

const RedirectIfAuthenticated = () => {
    const navigate = useNavigate();
    const [authenticated] = useAtom(authenticatedAtom);

    useEffect(() => {
        if (authenticated && (window.location.pathname === '/' || window.location.pathname === '/' || window.location.pathname === '/')) {
            navigate('/farmer-dashboard-demo');
        }
    }, [authenticated, navigate]);

    return null;
};

function App() {
    const [user, setUser] = useAtom(userAtom);
    const [authenticated, setAuthenticated] = useAtom(authenticatedAtom);
    const [loading, setLoading] = useState(true);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isDesktop] = useMediaQuery(["(min-width: 992px)"], {
        fallback: [false],
        ssr: true,
    });
    useEffect(() => {
        setIsSidebarCollapsed(!isDesktop);
    }, [isDesktop]);
    useEffect(() => {
        const checkAuth = async () => {

            // ✅ DEMO MODE — skip backend auth completely
            if (DEMO_MODE) {
                const demoUser = {
                    id: "demo-user",
                    name: "Demo",
                    lastName: "User",
                    email: "demo@agxes.com",
                    role: "demo", // give full access for demo
                };

                setUser(demoUser);
                setAuthenticated(true);
                setLoading(false);
                return;
            }

            // ✅ NORMAL AUTH FLOW
            const token = localStorage.getItem('token');

            if (token) {
                try {
                    const response = await fetch(`${baseUrl}/auth/user/me`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (response.ok) {
                        const userData = await response.json();
                        setUser(userData);
                        setAuthenticated(true);
                    } else {
                        localStorage.removeItem('token');
                        setUser(null);
                        setAuthenticated(false);
                    }
                } catch (error) {
                    console.error("Error verifying token:", error);
                    localStorage.removeItem('token');
                    setUser(null);
                    setAuthenticated(false);
                }
            } else {
                setUser(null);
                setAuthenticated(false);
            }

            setLoading(false);
        };

        checkAuth();
    }, [setUser, setAuthenticated]);

    const allowAccess = DEMO_MODE || authenticated;

    return (
        <Provider theme={theme}>
            {loading ? (
                <Center h="100vh" w="100vw" bg="gray.50">
                    <VStack spacing={6}>
                        <Image src="/logo_horizontal.png" alt="Agxes Logo" boxSize="100px" fit="contain"/>
                        <Spinner size="xl" thickness="4px" speed="0.7s" color="agxes.primary"/>
                    </VStack>
                </Center>
            ) : (
                <BrowserRouter>
                    <Flex direction="column" height="100vh">
                        {/* Fixed Header */}
                        {allowAccess && (
                            <Box
                                position="fixed"
                                top={0}
                                left={0}
                                right={0}
                                zIndex={"max"} // Ensure header is above other content
                                bg="white" // Add background color to avoid transparency issues
                                boxShadow="sm" // Optional: Add shadow for better separation
                            >
                                <DemoHeader/>
                            </Box>
                        )}

                        {/* Main Content Area */}
                        <Flex flexGrow={1}
                              mt={allowAccess ? "60px" : "0"}> {/* Adjust margin-top to account for header height */}
                            {/* Fixed Sidebar */}
                            {allowAccess && (
                                <Box
                                    position="fixed"
                                    left={0}
                                    top="60px"
                                    // top="60px" // Position below the header
                                    bottom={0}
                                    width={isSidebarCollapsed ? '56px' : '208px'}
                                    overflowY="hidden"
                                    overflowX="hidden"
                                    bg="gray.20"
                                    borderWidth="1px"
                                    zIndex={5} // Ensure sidebar is above main content but below header
                                >
                                    <DemoSidebar
                                        isCollapsed={isSidebarCollapsed}
                                        setIsSidebarCollapsed={setIsSidebarCollapsed}
                                    />
                                </Box>
                            )}

                            {/* Scrollable Main Content */}
                            <Box
                                flexGrow={1}
                                ml={allowAccess ? (isSidebarCollapsed ? '56px' : '208px') : '0'}
                                overflowY="auto" // Allow scrolling in the main content
                            >
                                <Routes>

                                    <Route path='/'
                                           element={allowAccess ? <Navigate to="/farmer-dashboard-demo"/> :
                                               <Landing/>}/>

                                    {/*    DEMO*/}
                                    <Route path='/demo-profile'
                                           element={allowAccess ? <DemoProfile/> : <Navigate to="/"/>}/>
                                    <Route path="/farmer-dashboard-demo"
                                           element={allowAccess ? <DemoFarmerDashboard/> :
                                               <Navigate to="/"/>}/>
                                    <Route path="/satellite-imagery-demo"
                                           element={allowAccess ? <DemoSatelliteImagery/> : <Navigate to="/"/>}/>
                                    <Route path="/financial-analysis-demo"
                                           element={allowAccess ? <DemoFinancialAnalysis/> :
                                               <Navigate to="/"/>}/>
                                    <Route path="/weather-info-demo"
                                           element={allowAccess ? <DemoWeather/> :
                                               <Navigate to="/"/>}/>
                                </Routes>
                            </Box>
                        </Flex>

                        {/* Footer */}
                        {allowAccess && (
                            <Box
                                ml={allowAccess ? (isSidebarCollapsed ? '56px' : '208px') : '0'}
                                mt="auto" // Push footer to the bottom
                            >
                                <DemoFooter/>
                            </Box>
                        )}
                    </Flex>
                </BrowserRouter>
            )}
            <Toaster/>
        </Provider>
    );
}

export default App;